import React, { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Signal, Zap, ShoppingBag, ShieldCheck, Check, Upload, FileText, ArrowLeft, ArrowRight, Gift, Truck, Sparkles, Eye, EyeOff, Loader2 } from "lucide-react";
import { startGoogle, useGoogleError } from "@/lib/googleAccess";
import { CompanyIdentity } from "@/components/CompanyIdentity";
import { PublicRegistration } from "@/components/PublicRegistration";
import { RegistrationLeaveGuard } from "@/components/RegistrationLeaveGuard";
import { Link, useLocation } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { InstallAppBanner, InstallAppButton } from "@/components/PwaInstall";
import { safeLocalStorage as localStorage, safeSessionStorage as sessionStorage } from "@/lib/safeStorage";

const HERO = "https://images.unsplash.com/photo-1753385158278-387d4dda60c4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const STEPS = ["Account", "Azienda", "Visura"];
const BRANDS = [
  { id: "enterimprese", name: "enterimprese", domain: "enterimprese.it", href: "https://ENTERIMPRESE.IT", src: "/brands/enterimprese.png" },
  { id: "venergia", name: "venergia", domain: "venergia.it", href: "https://VENERGIA.IT", src: "/brands/venergia-home.png" },
  { id: "vediloo", name: "vediloo", domain: "vediloo.it", href: "https://vediloo.it", src: "/brands/vediloo.png" },
  { id: "simamma", name: "simamma", domain: "simamma.com", href: "https://simamma.com", src: "/brands/simamma.svg" },
  { id: "retelit", name: "retelit", domain: "retelit.it", href: "https://retelit.it", src: "/brands/retelit.svg" },
  { id: "openapi", name: "openapi", domain: "openapi.com", href: "https://openapi.com", src: "/brands/openapi.svg" },
];
const genPass = () => "Mk" + Math.random().toString(36).slice(2, 8) + Math.floor(10 + Math.random() * 89) + "!";

export default function Landing() {
  const { login, verify2fa, register, refresh, formatApiErrorDetail } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => { const params = new URLSearchParams(location.search); setTab(params.get("tab") === "register" || ["invite", "agent", "ref"].some((key) => params.has(key)) ? "register" : "login"); }, [location.key, location.search, location.state]);
  const [tab, setTab] = useState(() => { const params = new URLSearchParams(location.search); return params.get("tab") === "register" || ["invite", "agent", "ref"].some((key) => params.has(key)) ? "register" : "login"; });
  const [loading, setLoading] = useState(false);
  const [registrationDirty, setRegistrationDirty] = useState(false);
  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [forgot, setForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [twofa, setTwofa] = useState(null); // {challenge, channel, sent_to}
  const [otpCode, setOtpCode] = useState("");
  const [rememberDevice, setRememberDevice] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const [googleError, clearGoogleError] = useGoogleError();
  const [googleBusy, setGoogleBusy] = useState(false);

  const [role, setRole] = useState("dealer");
  const [step, setStep] = useState(0);
  const [accountCreated, setAccountCreated] = useState(false);
  const [reg, setReg] = useState({ email: "", password: genPass(), name: "", phone: "", company_name: "", vat_number: "", address: "", company_description: "", ref: "", customer_type: "privato" });
  const [uploaded, setUploaded] = useState([]);
  const fileRef = useRef();
  const [showPass, setShowPass] = useState(true);
  const [extracting, setExtracting] = useState(false);
  const visuraRef = useRef();
  const extractVisura = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !(await allowRegistration())) return;
    const reader = new FileReader();
    reader.onload = async () => {
      setExtracting(true);
      try {
        const { data } = await api.post("/company/extract-visura", { file_base64: reader.result, filename: file.name });
        const d = data.data || {};
        setReg((r) => ({
          ...r,
          company_name: d.ragione_sociale || r.company_name,
          vat_number: d.partita_iva || r.vat_number,
          address: [d.sede_indirizzo, d.sede_cap, d.sede_citta, d.sede_provincia].filter(Boolean).join(" ") || r.address,
          company_description: d.descrizione || r.company_description,
          name: r.name || d.legale_rappresentante || "",
        }));
        toast.success(data.fields_found ? "Dati azienda compilati dalla visura!" : "Visura letta: controlla i dati");
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore lettura visura");
      } finally { setExtracting(false); if (visuraRef.current) visuraRef.current.value = ""; }
    };
    reader.readAsDataURL(file);
  };
  const visuraBox = (
    <div className="rounded-lg border border-primary/40 bg-primary/5 p-3 space-y-2" data-testid="visura-autocomplete">
      <p className="text-sm font-medium flex items-center gap-1.5"><Sparkles className="w-4 h-4 text-primary" /> Compila in automatico dalla visura</p>
      <input ref={visuraRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" className="hidden" onChange={extractVisura} data-testid="visura-autocomplete-input" />
      <Button type="button" variant="outline" size="sm" className="gap-2" disabled={extracting} onClick={() => visuraRef.current?.click()} data-testid="visura-autocomplete-button">
        {extracting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} {extracting ? "Lettura…" : "Carica visura"}
      </Button>
    </div>
  );

  useEffect(() => {
    const code = localStorage.getItem("markoo_ref");
    if (code) { setReg((r) => ({ ...r, ref: code })); setTab("register"); }
    const params = new URLSearchParams(window.location.search);
    if (params.get("agent")) {
      setTab("register");
      setRole(params.get("type") === "customer" ? "customer" : "dealer");
    }
    const pending = sessionStorage.getItem("markoo_2fa");
    if (pending) {
      try { const d = JSON.parse(pending); setTwofa(d); setResendIn(d.resend_in || 30); setTab("login"); } catch {}
      sessionStorage.removeItem("markoo_2fa");
    }
  }, []);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setInterval(() => setResendIn((v) => v - 1), 1000);
    return () => clearInterval(t);
  }, [resendIn]);

  const doLogin = async (email, password) => {
    clearGoogleError();
    setLoading(true);
    try {
      const res = await login(email, password);
      if (res && res.twofa_required) { setTwofa(res); setResendIn(res.resend_in || 30); toast.success("Ti abbiamo inviato un codice di verifica"); return; }
      toast.success("Accesso effettuato");
      navigate("/app/dashboard");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore di accesso");
    } finally { setLoading(false); }
  };
  const submitLogin = (e) => { e.preventDefault(); doLogin(loginData.email, loginData.password); };

  const submit2fa = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await verify2fa(twofa.challenge, otpCode, rememberDevice);
      toast.success("Accesso effettuato");
      navigate("/app/dashboard");
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Codice non valido");
    } finally { setLoading(false); }
  };
  const resend2fa = async () => {
    try {
      const { data } = await api.post("/auth/login/resend-2fa", { challenge: twofa.challenge });
      setTwofa(data); setResendIn(data.resend_in || 30); setOtpCode("");
      toast.success("Nuovo codice inviato");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore invio codice"); }
  };
  const cancel2fa = () => { setTwofa(null); setOtpCode(""); setRememberDevice(false); setResendIn(0); };

  const loginGoogle = async () => {
    clearGoogleError();
    setGoogleBusy(true);
    try { await startGoogle("login"); }
    catch (error) { setGoogleBusy(false); toast.error(formatApiErrorDetail(error.response?.data?.detail) || error.message); }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail });
      toast.success("Se l'email esiste, riceverai un link di reset.");
      setForgot(false); setForgotEmail("");
    } catch { toast.error("Errore, riprova più tardi."); }
    finally { setLoading(false); }
  };

  const registerCustomer = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (!(await allowRegistration())) return;
      await register({ ...reg, role: "customer", agent_code: new URLSearchParams(window.location.search).get("agent") || undefined });
      localStorage.removeItem("markoo_ref");
      toast.success("Registrazione completata");
      navigate("/app/dashboard");
    } catch (err) {
      registrationError(err);
    } finally { setLoading(false); }
  };

  const registerAgency = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (!(await allowRegistration())) return;
      await api.post("/auth/register", { ...reg, role: "agent" });
      toast.success("Registrazione agenzia completata. Ora puoi accedere.");
      setTab("login");
    } catch (err) {
      registrationError(err);
    } finally { setLoading(false); }
  };

  const nextFromAccount = async (e) => {
    e.preventDefault();
    if (!(await allowRegistration())) return;
    if (!reg.name || !reg.email || !reg.password) { toast.error("Compila i campi obbligatori"); return; }
    setStep(1);
  };

  const createDealerAccount = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (!(await allowRegistration())) return;
      // Register WITHOUT triggering the guest->dashboard redirect (we don't set auth user yet).
      await api.post("/auth/register", { ...reg, role: "dealer", agent_code: new URLSearchParams(window.location.search).get("agent") || undefined });
      localStorage.removeItem("markoo_ref");
      setAccountCreated(true);
      toast.success("Account creato! Ora carica la visura.");
      setStep(2);
    } catch (err) {
      registrationError(err);
    } finally { setLoading(false); }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("doc_type", "visura");
    try {
      const { data } = await api.post("/uploads/document", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUploaded((u) => [...u, data]);
      toast.success("Visura caricata");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore caricamento");
    } finally { setLoading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const finishWizard = async () => {
    await refresh();
    navigate("/app/dashboard");
  };

  const [regOpen, setRegOpen] = useState(null);
  const [featuresError, setFeaturesError] = useState(false);
  const featureRequest = useRef(0);
  const loadFeatures = async () => {
    const id = ++featureRequest.current;
    try {
      const { data } = await api.get("/settings/features");
      if (id === featureRequest.current) { setRegOpen(data.registrations_open === true); setFeaturesError(false); }
      return data.registrations_open === true;
    } catch {
      if (id === featureRequest.current) { setRegOpen(null); setFeaturesError(true); }
      return false;
    }
  };
  useEffect(() => {
    loadFeatures();
    const recheck = () => { if (document.visibilityState === "visible") loadFeatures(); };
    window.addEventListener("focus", recheck);
    document.addEventListener("visibilitychange", recheck);
    return () => { featureRequest.current++; window.removeEventListener("focus", recheck); document.removeEventListener("visibilitychange", recheck); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (regOpen !== true && !accountCreated) {
      setReg((current) => ({ email: "", password: genPass(), name: "", phone: "", company_name: "", vat_number: "", address: "", company_description: "", ref: current.ref, customer_type: "privato" }));
      setStep(0); setUploaded([]);
    }
  }, [regOpen, accountCreated]);
  const allowRegistration = async () => {
    if (await loadFeatures()) return true;
    toast.error("Le nuove registrazioni non sono disponibili. Puoi accedere con un account già esistente.");
    return false;
  };
  const registrationError = (err) => {
    const message = formatApiErrorDetail(err.response?.data?.detail) || "Errore di registrazione";
    if (err.response?.status === 403 && message.includes("registrazioni")) setRegOpen(false);
    toast.error(message);
  };


  return (
    <RegistrationLeaveGuard dirty={tab === "register" && registrationDirty}>
    <div className={`min-h-screen grid ${tab === "register" ? "grid-cols-1 grid-rows-[1fr_auto]" : "lg:grid-cols-2"} bg-background`} data-testid="landing-layout">
      {tab !== "register" && <div className="relative order-2 lg:order-1 flex flex-col gap-4 px-4 pb-5 sm:px-6 lg:p-6 lg:min-h-screen" data-testid="landing-left-column">
        <img src={HERO} alt="SIM e telecom" className="absolute inset-0 hidden lg:block w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 hidden lg:block gradient-blue" />
        <div className="absolute inset-0 hidden lg:block bg-background/40" />
        <div className="relative z-10 hidden lg:block">
          <Logo size={28} />
        </div>
        <div className="relative z-10 hidden lg:block max-w-md">
          <h1 className="font-head text-2xl xl:text-3xl font-bold tracking-tight leading-tight" data-testid="landing-marketing-title">La piattaforma per i tuoi negozi e agenzie</h1>
          <p className="text-muted-foreground mt-2 text-sm leading-relaxed" data-testid="landing-marketing-description">Registra i tuoi dealer, carica la visura, vendi SIM ed eSIM, spedisci pacchi, offri gift card e ricariche — tutto in un unico posto.</p>
          <div className="grid grid-cols-2 gap-x-3 gap-y-2 mt-4" data-testid="landing-service-list">
            {[{ icon: Signal, t: "Attivazione SIM & eSIM" }, { icon: Zap, t: "Ricariche istantanee" }, { icon: Truck, t: "Servizio spedizioni" }, { icon: Gift, t: "Gift card & voucher" }, { icon: ShoppingBag, t: "Catalogo prodotti" }, { icon: Sparkles, t: "e molti altri servizi" }].map((f) => (
              <div key={f.t} className="flex items-center gap-2 text-xs min-w-0" data-testid={`landing-service-${f.t.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <div className="w-7 h-7 shrink-0 rounded-lg bg-primary/15 text-primary flex items-center justify-center"><f.icon className="w-4 h-4" /></div>{f.t}
              </div>
            ))}
          </div>
        </div>
        <div className="relative z-10 space-y-3" data-testid="landing-presentation-actions">
          <div className="grid grid-cols-2 gap-2" data-testid="landing-info-actions"><Button asChild size="sm" className="rounded-full !w-full !min-w-0 text-xs" data-testid="landing-btn-come-funziona"><Link to="/overview#come-funziona">Come funziona<ArrowRight className="w-4 h-4 ml-1 shrink-0" /></Link></Button><Button asChild size="sm" variant="outline" className="rounded-full border-primary/30 !w-full !min-w-0 text-xs" data-testid="landing-btn-i-nostri-servizi"><Link to="/overview#i-nostri-servizi">I nostri servizi<ArrowRight className="w-4 h-4 ml-1 shrink-0" /></Link></Button></div>
        </div>
        <section className="relative z-10 w-full mx-auto lg:mx-0 border-t border-border/60 pt-3" aria-labelledby="brands-services-heading" data-testid="brands-services-section">
          <h2 id="brands-services-heading" className="font-head text-sm font-semibold mb-2 text-muted-foreground" data-testid="brands-services-heading">Marchi e servizi</h2>
          <div className="grid grid-cols-3 gap-x-2 gap-y-1.5">
            {BRANDS.map((brand) => <a key={brand.id} href={brand.href} target="_blank" rel="noopener noreferrer" aria-label={`${brand.name} — apri il sito in una nuova scheda`} data-testid={`brand-link-${brand.id}`} className="group min-w-0 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background">
              <div className="h-8 flex items-center justify-center rounded-md bg-white px-2 transition-transform group-hover:-translate-y-0.5">
                <img src={brand.src} alt={brand.name} width="100" height="24" className="max-w-full w-auto h-5 object-contain" data-testid={`brand-logo-${brand.id}`} />
              </div>
              <span className="block mt-1 text-center text-[9px] text-muted-foreground group-hover:text-foreground transition-colors" data-testid={`brand-domain-${brand.id}`}>{brand.domain}</span>
            </a>)}
          </div>
        </section>
      </div>}

      <div className={`order-1 lg:order-2 flex ${tab === "register" ? "items-start px-4 py-6 sm:px-8 lg:px-10" : "items-center p-4 sm:p-10"} justify-center min-w-0`} data-testid="landing-auth-column">
        <div className={`w-full ${tab === "register" ? "max-w-screen-2xl" : "max-w-md"}`}>
          {tab === "register" && <header className="mb-6" data-testid="registration-wide-header"><Link to="/" aria-label="Torna alla homepage" className="inline-flex rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background" data-testid="registration-home-logo"><Logo size={24} /></Link><h1 className="font-head text-2xl sm:text-3xl font-semibold mt-4" data-testid="registration-wide-title">Crea il tuo account</h1><p className="text-sm text-muted-foreground mt-2" data-testid="registration-wide-description">Scegli il profilo e compila i dati. Puoi caricare la visura per aiutarti nella compilazione.</p></header>}
          {tab !== "register" && <InstallAppBanner />}
          <div className={`${tab === "register" ? "hidden" : "lg:hidden"} mb-6 flex flex-wrap items-center gap-3`}>
            <Logo size={26} />
            <InstallAppButton onlyAfterDismiss testId="pwa-install-login" />
          </div>
          {tab === "register" ? <PublicRegistration key={location.key} onDirtyChange={setRegistrationDirty} /> : <Tabs value={tab} onValueChange={(value) => { setTab(value); const params = new URLSearchParams(location.search); if (value === "register") { params.set("tab", "register"); loadFeatures(); } else { params.delete("tab"); } navigate({ pathname: location.pathname, search: params.toString(), hash: location.hash }); }}>
            <TabsList className={`sticky top-3 z-20 grid grid-cols-2 w-full ${tab === "register" ? "max-w-md" : ""} mb-6 bg-muted shadow-sm`} data-testid="auth-navigation-tabs">
              <TabsTrigger value="login" className="flex-1 !basis-0" data-testid="tab-login">Accedi</TabsTrigger>
              <TabsTrigger value="register" className="flex-1 !basis-0" data-testid="tab-register">Registrati</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              {twofa ? (
              <form onSubmit={submit2fa} className="space-y-4" data-testid="twofa-form">
                <div className="rounded-xl border border-primary/40 bg-primary/5 p-4">
                  <p className="text-sm font-medium flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-primary" /> Verifica in due passaggi</p>
                  <p className="text-xs text-muted-foreground mt-1">Abbiamo inviato un codice a 6 cifre {twofa.channel === "sms" ? "via SMS al numero" : "via email a"} <span className="font-medium">{twofa.sent_to}</span>.</p>
                </div>
                <div>
                  <Label htmlFor="otp">Codice di verifica</Label>
                  <Input id="otp" inputMode="numeric" maxLength={6} required data-testid="twofa-code-input" className="mt-1.5 tracking-[0.4em] text-center font-mono text-lg" value={otpCode} onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="000000" />
                </div>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" data-testid="twofa-remember-device" checked={rememberDevice} onChange={(e) => setRememberDevice(e.target.checked)} className="w-4 h-4 accent-primary" />
                  Ricorda questo dispositivo per 30 giorni
                </label>
                <Button type="submit" disabled={loading || otpCode.length !== 6} className="w-full" data-testid="twofa-verify-button">{loading ? "Verifica…" : "Verifica e accedi"}</Button>
                <div className="flex flex-wrap gap-2 items-center justify-between text-xs">
                  <button type="button" onClick={cancel2fa} className="app-text-button text-muted-foreground hover:underline" data-testid="twofa-back-button">← Torna al login</button>
                  <button type="button" disabled={resendIn > 0} onClick={resend2fa} className="app-text-button text-primary hover:underline disabled:opacity-50 disabled:no-underline" data-testid="twofa-resend-button">{resendIn > 0 ? `Reinvia codice (${resendIn}s)` : "Reinvia codice"}</button>
                </div>
              </form>
              ) : (
              <>
              <form onSubmit={submitLogin} className="space-y-4">
                <div>
                  <Label htmlFor="l-email">Email</Label>
                  <Input id="l-email" type="email" required data-testid="login-email" className="mt-1.5" value={loginData.email} onChange={(e) => setLoginData({ ...loginData, email: e.target.value })} placeholder="nome@azienda.it" />
                </div>
                <div>
                  <Label htmlFor="l-pass">Password</Label>
                  <Input id="l-pass" type="password" required data-testid="login-password" className="mt-1.5" value={loginData.password} onChange={(e) => setLoginData({ ...loginData, password: e.target.value })} placeholder="••••••••" />
                </div>
                <Button type="submit" disabled={loading} className="w-full" data-testid="login-submit-button">{loading ? "Accesso…" : "Accedi"}</Button>
                <button type="button" onClick={() => { setForgot((v) => !v); setForgotEmail(loginData.email); }} className="app-text-button text-xs text-primary hover:underline mx-auto block mt-1" data-testid="login-forgot-password-link">Password dimenticata?</button>
              </form>
              {forgot && (
                <form onSubmit={submitForgot} className="mt-4 space-y-3 rounded-xl border border-border p-4" data-testid="forgot-form">
                  <p className="text-sm text-muted-foreground">Inserisci la tua email: ti invieremo un link per reimpostare la password (valido 1 ora).</p>
                  <Input type="email" required data-testid="forgot-email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} placeholder="nome@azienda.it" />
                  <Button type="submit" disabled={loading} className="w-full" data-testid="forgot-submit">{loading ? "Invio…" : "Invia link di reset"}</Button>
                </form>
              )}
              <div className="relative my-4">
                <div className="border-t border-border" />
                <span className="absolute left-1/2 -translate-x-1/2 -top-2.5 bg-card px-2 text-xs text-muted-foreground">oppure</span>
              </div>
              <Button type="button" variant="outline" className="w-full gap-2" data-testid="google-login-button" disabled={googleBusy} onClick={loginGoogle}>
                <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.9 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.1 29.5 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.1 29.5 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.5-5.2l-6.2-5.3C29.2 35.1 26.7 36 24 36c-5.3 0-9.7-3.1-11.3-7.9l-6.5 5C9.6 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.5l6.2 5.3C41.9 35.6 44 30.3 44 24c0-1.3-.1-2.3-.4-3.5z"/></svg>
                {googleBusy ? "Apertura Google…" : "Accedi con Google"}
              </Button>
              {googleError && <p role="alert" className="mt-3 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive" data-testid="google-login-error">{googleError}</p>}
              <p className="text-xs text-muted-foreground mt-2" data-testid="google-existing-users-note">Google è disponibile solo per gli account già registrati e abilitati. Non crea nuove registrazioni.</p>
              </>
              )}
            </TabsContent>

            {false && <TabsContent value="legacy-register">
              {regOpen === true || accountCreated ? <>
              {reg.ref && (
                <div className="mb-4 rounded-xl border border-primary/40 bg-primary/10 px-4 py-3 flex items-center gap-2 text-sm" data-testid="referral-invite-banner">
                  <Gift className="w-4 h-4 text-primary shrink-0" />
                  <span>Invito attivo: <span className="font-mono font-semibold">{reg.ref}</span> — 10% di sconto sul primo ordine.</span>
                </div>
              )}
              <div className="flex gap-2 mb-4">
                <Button type="button" variant={role === "dealer" ? "default" : "outline"} className="flex-1" data-testid="role-dealer" onClick={() => { setRole("dealer"); setStep(0); }}>Dealer</Button>
                <Button type="button" variant={role === "customer" ? "default" : "outline"} className="flex-1" data-testid="role-customer" onClick={() => setRole("customer")}>Cliente</Button>
                <Button type="button" variant={role === "agent" ? "default" : "outline"} className="flex-1" data-testid="role-agent" onClick={() => setRole("agent")}>Agenzia</Button>
              </div>

              {role === "agent" && (
                <form onSubmit={registerAgency} className="space-y-3" data-testid="agency-register-form">
                  {visuraBox}
                  <p className="text-sm text-muted-foreground">Registra la tua agenzia commerciale: presenti dealer, master e clienti e guadagni provvigioni.</p>
                  <div><Label>Nome agenzia / referente</Label><Input required data-testid="register-name" className="mt-1.5" value={reg.name} onChange={(e) => setReg({ ...reg, name: e.target.value })} /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Ragione sociale</Label><Input data-testid="register-company" className="mt-1.5" value={reg.company_name} onChange={(e) => setReg({ ...reg, company_name: e.target.value })} /></div>
                    <div><Label>Partita IVA</Label><Input data-testid="register-vat" className="mt-1.5" value={reg.vat_number} onChange={(e) => setReg({ ...reg, vat_number: e.target.value })} /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Email</Label><Input type="email" required data-testid="register-email" className="mt-1.5" value={reg.email} onChange={(e) => setReg({ ...reg, email: e.target.value })} /></div>
                    <div><Label>Password</Label>                  <div className="relative mt-1.5">
                    <Input type={showPass ? "text" : "password"} required data-testid="register-password" value={reg.password} onChange={(e) => setReg({ ...reg, password: e.target.value })} className="pr-9" />
                    <button type="button" onClick={() => setShowPass((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground" data-testid="toggle-password">{showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
                  </div></div>
                  </div>
                  <div><Label>Telefono</Label><Input type="tel" data-testid="register-phone" className="mt-1.5" value={reg.phone} onChange={(e) => setReg({ ...reg, phone: e.target.value })} /></div>
                  <Button type="submit" disabled={loading} className="w-full" data-testid="agency-register-submit">{loading ? "Registrazione…" : "Crea account agenzia"}</Button>
                </form>
              )}

              {role === "customer" && (
                <form onSubmit={registerCustomer} className="space-y-3">
                  <div className="flex gap-2">
                    <Button type="button" variant={reg.customer_type === "privato" ? "default" : "outline"} className="flex-1" data-testid="customer-type-privato" onClick={() => setReg({ ...reg, customer_type: "privato" })}>Privato</Button>
                    <Button type="button" variant={reg.customer_type === "azienda" ? "default" : "outline"} className="flex-1" data-testid="customer-type-azienda" onClick={() => setReg({ ...reg, customer_type: "azienda" })}>Azienda</Button>
                  </div>
                  <div><Label>{reg.customer_type === "azienda" ? "Referente" : "Nome e cognome"}</Label><Input required data-testid="register-name" className="mt-1.5" value={reg.name} onChange={(e) => setReg({ ...reg, name: e.target.value })} /></div>
                  {reg.customer_type === "azienda" && (
                    <>
                    {visuraBox}
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label>Ragione sociale</Label><Input required data-testid="register-company" className="mt-1.5" value={reg.company_name} onChange={(e) => setReg({ ...reg, company_name: e.target.value })} /></div>
                      <div><Label>Partita IVA</Label><Input required data-testid="register-vat" className="mt-1.5" value={reg.vat_number} onChange={(e) => setReg({ ...reg, vat_number: e.target.value })} /></div>
                    </div>
                    </>
                  )}
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Email</Label><Input type="email" required data-testid="register-email" className="mt-1.5" value={reg.email} onChange={(e) => setReg({ ...reg, email: e.target.value })} /></div>
                    <div><Label>Password</Label>                  <div className="relative mt-1.5">
                    <Input type={showPass ? "text" : "password"} required data-testid="register-password" value={reg.password} onChange={(e) => setReg({ ...reg, password: e.target.value })} className="pr-9" />
                    <button type="button" onClick={() => setShowPass((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground" data-testid="toggle-password">{showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
                  </div></div>
                  </div>
                  <div><Label>Telefono</Label><Input type="tel" data-testid="register-phone" className="mt-1.5" value={reg.phone} onChange={(e) => setReg({ ...reg, phone: e.target.value })} /></div>
                  <Button type="submit" disabled={loading} className="w-full" data-testid="register-submit-button">{loading ? "Registrazione…" : "Crea account"}</Button>
                </form>
              )}

              {role === "dealer" && (
                <div data-testid="dealer-wizard">
                  {/* Stepper */}
                  <div className="flex items-center justify-between mb-6">
                    {STEPS.map((s, i) => (
                      <div key={s} className="flex items-center flex-1 last:flex-none">
                        <div className={`flex items-center gap-2 ${i <= step ? "text-primary" : "text-muted-foreground"}`}>
                          <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border ${i < step ? "bg-primary border-primary text-primary-foreground" : i === step ? "border-primary" : "border-border"}`}>
                            {i < step ? <Check className="w-4 h-4" /> : i + 1}
                          </div>
                          <span className="text-xs font-medium hidden sm:inline">{s}</span>
                        </div>
                        {i < STEPS.length - 1 && <div className={`flex-1 h-px mx-2 ${i < step ? "bg-primary" : "bg-border"}`} />}
                      </div>
                    ))}
                  </div>

                  {step === 0 && (
                    <form onSubmit={nextFromAccount} className="space-y-3">
                      <div><Label>Nome e cognome referente</Label><Input required data-testid="register-name" className="mt-1.5" value={reg.name} onChange={(e) => setReg({ ...reg, name: e.target.value })} /></div>
                      <div className="grid grid-cols-2 gap-3">
                        <div><Label>Email</Label><Input type="email" required data-testid="register-email" className="mt-1.5" value={reg.email} onChange={(e) => setReg({ ...reg, email: e.target.value })} /></div>
                        <div><Label>Password</Label>                  <div className="relative mt-1.5">
                    <Input type={showPass ? "text" : "password"} required data-testid="register-password" value={reg.password} onChange={(e) => setReg({ ...reg, password: e.target.value })} className="pr-9" />
                    <button type="button" onClick={() => setShowPass((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground" data-testid="toggle-password">{showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
                  </div></div>
                      </div>
                      <div><Label>Telefono</Label><Input type="tel" data-testid="register-phone" className="mt-1.5" value={reg.phone} onChange={(e) => setReg({ ...reg, phone: e.target.value })} /></div>
                      <Button type="submit" className="w-full" data-testid="wizard-next-account">Continua</Button>
                    </form>
                  )}

                  {step === 1 && (
                    <form onSubmit={createDealerAccount} className="space-y-3">
                      {visuraBox}
                      <div><Label>Ragione sociale</Label><Input required data-testid="register-company" className="mt-1.5" value={reg.company_name} onChange={(e) => setReg({ ...reg, company_name: e.target.value })} placeholder="Es. Rossi Telecom Srl" /></div>
                      <div className="grid grid-cols-2 gap-3">
                        <div><Label>Partita IVA</Label><Input required data-testid="register-vat" className="mt-1.5" value={reg.vat_number} onChange={(e) => setReg({ ...reg, vat_number: e.target.value })} /></div>
                        <div><Label>Indirizzo</Label><Input data-testid="register-address" className="mt-1.5" value={reg.address} onChange={(e) => setReg({ ...reg, address: e.target.value })} /></div>
                      </div>
                      <div><Label>Descrizione azienda (soci, licenze, oggetto sociale…)</Label>
                        <textarea data-testid="register-company-description" rows={4} className="mt-1.5 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={reg.company_description} onChange={(e) => setReg({ ...reg, company_description: e.target.value })} placeholder="Compilata in automatico dalla visura; puoi modificarla." />
                      </div>
                      <div className="flex gap-2">
                        <Button type="button" variant="outline" className="gap-1" onClick={() => setStep(0)} data-testid="wizard-back"><ArrowLeft className="w-4 h-4" /> Indietro</Button>
                        <Button type="submit" disabled={loading} className="flex-1" data-testid="wizard-create-account">{loading ? "Creazione…" : "Crea account e continua"}</Button>
                      </div>
                    </form>
                  )}

                  {step === 2 && (
                    <div className="space-y-4">
                      <Card className="p-5 bg-secondary/40 text-center">
                        <div className="w-12 h-12 rounded-xl bg-primary/15 text-primary flex items-center justify-center mx-auto"><Upload className="w-6 h-6" /></div>
                        <p className="font-head font-semibold mt-3">Carica la visura camerale</p>
                        <p className="text-sm text-muted-foreground mt-1">PDF, PNG o JPG. L'admin la verificherà per approvare il tuo account.</p>
                        <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" className="hidden" onChange={onUpload} data-testid="dealer-register-visura-upload" />
                        <Button className="mt-4 gap-2" disabled={loading} onClick={() => fileRef.current?.click()} data-testid="wizard-upload-button"><Upload className="w-4 h-4" /> {loading ? "Caricamento…" : "Seleziona file"}</Button>
                      </Card>
                      {uploaded.map((u) => (
                        <div key={u.id} className="flex items-center gap-2 p-3 rounded-lg bg-secondary/40 text-sm" data-testid={`wizard-uploaded-${u.id}`}>
                          <FileText className="w-4 h-4 text-teal-500" /> {u.original_filename}
                        </div>
                      ))}
                      <Button className="w-full" onClick={finishWizard} data-testid="wizard-finish-button">
                        {uploaded.length ? "Completa e vai alla dashboard" : "Salta per ora e vai alla dashboard"}
                      </Button>
                      <p className="text-xs text-muted-foreground text-center">Potrai caricare o aggiornare i documenti anche dalla sezione Documenti.</p>
                    </div>
                  )}
                </div>
              )}
              </> : <div className="space-y-3 py-4" role="status" data-testid="registration-unavailable">
                <p className="font-medium" data-testid="registration-unavailable-title">{featuresError ? "Registrazione non disponibile" : regOpen === null ? "Verifica disponibilità registrazioni…" : "Registrazioni momentaneamente chiuse"}</p>
                <p className="text-sm text-muted-foreground" data-testid="registration-unavailable-description">{featuresError ? "Non è stato possibile verificare l’apertura delle registrazioni. Nessun modulo viene attivato finché il controllo non riesce." : regOpen === false ? "Non raccogliamo nuovi dati di registrazione. Se hai già un account, puoi continuare ad accedere." : "Attendi la verifica prima di inserire dati."}</p>
                {featuresError && <Button variant="outline" onClick={loadFeatures} data-testid="registration-features-retry">Riprova</Button>}
                <Button variant="outline" onClick={() => setTab("login")} data-testid="registration-return-login">Vai ad Accedi</Button>
              </div>}
            </TabsContent>}
          </Tabs>}
        </div>
      </div>
      <footer className="order-3 col-span-full border-t border-border px-4 sm:px-10 py-4" data-testid="landing-company-footer"><div className={tab === "register" ? "max-w-screen-2xl mx-auto" : ""}><CompanyIdentity testId="landing-company-identity" /></div></footer>
    </div>
    </RegistrationLeaveGuard>
  );
}
